import { calculatePrice } from '@/utils/product'
import { produce } from 'immer'
import React, { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

interface FormValues {
  selectedVariant?: string
  totalQuantity?: number
  subitems?: Record<string, Record<string, number>>
}

interface ProductState {
  product: Oms.MenuItem
  productState: Oms.MenuItem
  totalQuantity: number | null
  totalPrice: number | null
  selectedVariantId: string | null
  values: FormValues | null
}

const stateReducer = (values: FormValues) =>
  produce((draftState: ProductState) => {
    draftState.selectedVariantId =
      values.selectedVariant ||
      (draftState.productState.variants[0] || {}).id ||
      null
    draftState.totalPrice = calculatePrice(draftState.productState, values)
    draftState.totalQuantity = values.totalQuantity || null
    draftState.values = values
  })

export const useProductState = (product: Oms.MenuItem) => {
  const { getValues, clearErrors, watch } = useFormContext<FormValues>()
  const prevStateRef = useRef<ProductState | null>(null)

  const [state, setState] = React.useState(() =>
    stateReducer(getValues())({
      product,
      productState: product,
      totalQuantity: null,
      totalPrice: null,
      selectedVariantId: null,
      values: null,
    })
  )

  // Watch for form value changes
  const totalQuantity = watch('totalQuantity')
  const selectedVariant = watch('selectedVariant')
  const subitems = watch('subitems')
  // Update state when form values change
  useEffect(() => {
    const currentValues = {
      totalQuantity,
      selectedVariant,
      subitems,
    }
    const prevValues = state.values

    // Deep comparison of values
    const hasChanged =
      JSON.stringify(currentValues) !== JSON.stringify(prevValues)

    if (hasChanged) {
      setState((prevState: ProductState) =>
        stateReducer(currentValues)(prevState)
      )
    }
  }, [totalQuantity, selectedVariant, subitems])

  // Handle variant selection change
  const calculateVariantDiff = (variantId: string) => {
    const newValues = produce((draftState: FormValues) => {
      draftState.selectedVariant = variantId
    })(getValues())
    const { totalPrice } = stateReducer(newValues)(state)
    return (totalPrice ?? 0) - (state.totalPrice ?? 0)
  }

  // Handle option selection change
  const calculateOptionDiff = (optionId: string, subVariantId: string) => {
    const newValues = produce((draftState: FormValues) => {
      if (draftState.subitems === undefined) draftState.subitems = {}
      if (draftState.subitems[optionId] === undefined)
        draftState.subitems[optionId] = {}
      draftState.subitems[optionId][subVariantId] =
        (draftState.subitems[optionId][subVariantId] || 0) + 1
    })(getValues())
    const { totalPrice } = stateReducer(newValues)(state)
    return (totalPrice ?? 0) - (state.totalPrice ?? 0)
  }

  // Handle variant selection change
  useEffect(() => {
    if (prevStateRef.current?.selectedVariantId !== state.selectedVariantId) {
      clearErrors('selectedVariant')
    }
  }, [state.selectedVariantId, clearErrors])

  useEffect(() => {
    prevStateRef.current = state
  }, [state])

  return {
    ...state,
    calculateVariantDiff,
    calculateOptionDiff,
  }
}

export default useProductState
